import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Wallet, TrendingUp, Shield, Lock, DollarSign, 
  ArrowRight, CheckCircle, AlertTriangle, Building,
  Coins, BarChart3, PiggyBank, CreditCard, Scale
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SeedCapital = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [protocolStats, setProtocolStats] = useState(null);
  const [userPosition, setUserPosition] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      // Fetch protocol stats
      const statsRes = await fetch(`${API}/defi/protocol-stats`);
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setProtocolStats(stats);
      }

      // Fetch user position if logged in
      if (token) {
        const posRes = await fetch(`${API}/defi/position`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (posRes.ok) {
          const pos = await posRes.json();
          setUserPosition(pos);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const creditTiers = [
    {
      name: "Starter",
      minCollateral: "$10,000",
      maxCredit: "$100,000",
      apr: "10%",
      term: "90 days",
      color: "from-gray-500 to-gray-600"
    },
    {
      name: "Growth",
      minCollateral: "$100,000",
      maxCredit: "$500,000",
      apr: "8%",
      term: "180 days",
      color: "from-blue-500 to-blue-600",
      popular: true
    },
    {
      name: "Enterprise",
      minCollateral: "$500,000",
      maxCredit: "$2,000,000",
      apr: "6%",
      term: "365 days",
      color: "from-[#10b981] to-[#059669]"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]">
      {/* Compliance Banner */}
      <div className="bg-blue-500/10 border-b border-blue-500/20">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2">
          <Scale className="w-4 h-4 text-blue-400" />
          <span className="text-blue-400 text-sm">
            Private Placement • Reg D 506(b) Exempt • Accredited Investors Only
          </span>
          <a href="/compliance" className="text-blue-300 hover:text-blue-200 text-sm underline ml-2">
            View Compliance
          </a>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-gray-800 bg-[#050505]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#10b981]" />
            <div>
              <h1 className="text-xl font-bold text-white">Bethereum</h1>
              <p className="text-xs text-gray-500">Hybrid Seed Capital System</p>
            </div>
          </div>
          <nav className="flex items-center gap-4">
            <a href="/dashboard" className="text-gray-400 hover:text-white text-sm">Dashboard</a>
            <a href="/compliance" className="text-gray-400 hover:text-white text-sm">Compliance</a>
            {user ? (
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                {user.email?.split('@')[0]}
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} className="bg-[#10b981] hover:bg-[#059669]">
                Sign In
              </Button>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section with Background Image */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0 opacity-40"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/e8bab4bf-6551-45f6-918c-29c0407b9456/images/21224098d3ae02ba07fefaac3923ffe23c06dd5df1d06e313bcb2541fa33f380.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505] z-0" />
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10b981]/10 border border-[#10b981]/20 mb-6">
            <Building className="w-4 h-4 text-[#10b981]" />
            <span className="text-[#10b981] text-sm font-medium">Private Credit Infrastructure • Accredited Only</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Hybrid Private Credit<br />
            <span className="text-[#10b981]">Digital Infrastructure</span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            A private, permissioned digital credit infrastructure for accredited participants.
            Smart contract transparency. Non-custodial execution. Compliance-first design.
          </p>

          <div className="flex items-center justify-center gap-4 mb-12">
            <Button 
              size="lg" 
              className="bg-[#10b981] hover:bg-[#059669]"
              onClick={() => navigate(user ? '/defi' : '/login')}
            >
              <Wallet className="w-5 h-5 mr-2" />
              Access Credit Facility
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/compliance')}>
              <Scale className="w-5 h-5 mr-2" />
              View Compliance
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <p className="text-3xl font-bold text-white">1T</p>
              <p className="text-gray-400 text-sm">BETH Total Supply</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <p className="text-3xl font-bold text-[#10b981]">75%</p>
              <p className="text-gray-400 text-sm">Max LTV</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <p className="text-3xl font-bold text-blue-400">6-10%</p>
              <p className="text-gray-400 text-sm">APR Range</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <p className="text-3xl font-bold text-purple-400">$2M</p>
              <p className="text-gray-400 text-sm">Max Credit Line</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-gray-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            How the Credit System Works
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#10b981]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#10b981]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">1. Verify Status</h3>
              <p className="text-gray-400 text-sm">Complete KYC and accredited investor verification</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">2. Deposit Collateral</h3>
              <p className="text-gray-400 text-sm">Lock BETH tokens in the secure collateral vault</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">3. Access Credit</h3>
              <p className="text-gray-400 text-sm">Draw from your credit line up to 75% LTV</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">4. Repay & Grow</h3>
              <p className="text-gray-400 text-sm">Repay with interest and unlock your collateral</p>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Tiers */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Credit Tiers
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Choose the tier that matches your investment profile. Higher collateral unlocks better rates and limits.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {creditTiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`bg-gray-900/50 border-gray-800 relative ${tier.popular ? 'ring-2 ring-[#10b981]' : ''}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#10b981] text-white text-xs px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-4`}>
                    <Coins className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-white">{tier.name}</CardTitle>
                  <CardDescription>Credit Facility</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Min Collateral</span>
                      <span className="text-white font-semibold">{tier.minCollateral}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Max Credit</span>
                      <span className="text-white font-semibold">{tier.maxCredit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">APR</span>
                      <span className="text-[#10b981] font-semibold">{tier.apr}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Term</span>
                      <span className="text-white font-semibold">{tier.term}</span>
                    </div>
                    <Button 
                      className="w-full mt-4" 
                      variant={tier.popular ? "default" : "outline"}
                      onClick={() => navigate(user ? '/defi' : '/login')}
                    >
                      Apply Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Management */}
      <section className="py-16 px-4 bg-gray-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Risk Management Framework
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Liquidation Protection</h3>
                <p className="text-gray-400 text-sm">
                  Positions are monitored 24/7. Automatic liquidation triggers at 85% LTV 
                  protect the protocol and other users.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Oracle Price Feeds</h3>
                <p className="text-gray-400 text-sm">
                  Real-time price data from multiple oracle sources ensures accurate 
                  valuations and fair liquidations.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-xl bg-[#10b981]/20 flex items-center justify-center mb-4">
                  <PiggyBank className="w-6 h-6 text-[#10b981]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Reserve Fund</h3>
                <p className="text-gray-400 text-sm">
                  20% of protocol revenue is held in reserve to cover potential 
                  bad debt and ensure system stability.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Access Institutional Credit?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Join accredited investors who are leveraging the Bethereum Hybrid Seed Capital 
            System to grow their portfolios.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-[#10b981] hover:bg-[#059669]"
              onClick={() => navigate(user ? '/defi' : '/login')}
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/compliance')}>
              Read Compliance Docs
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#10b981]" />
              <span className="text-white font-semibold">Bethereum</span>
            </div>
            <p className="text-gray-500 text-sm text-center">
              Private Placement under Regulation D, Rule 506(b) • Accredited Investors Only
            </p>
            <div className="flex items-center gap-6">
              <a href="/terms" className="text-gray-400 hover:text-white text-sm">Terms</a>
              <a href="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy</a>
              <a href="/compliance" className="text-gray-400 hover:text-white text-sm">Compliance</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SeedCapital;
